import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Switch, Button, message, Space, Typography, Alert } from 'antd';
import { MailOutlined, SendOutlined, SaveOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';

const { Title } = Typography;

interface EmailSettings {
    id?: number;
    host: string;
    port: number;
    secure: boolean;
    auth: {
        user: string;
        pass: string;
    };
    from: string;
    fromName: string;
    replyTo?: string;
    isActive: boolean;
}

const EmailSettingsPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [settings, setSettings] = useState<EmailSettings | null>(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response: any = await apiService.get('/email-settings');
            if (response.data?.data) {
                const data = response.data.data;
                setSettings(data);
                form.setFieldsValue({
                    host: data.host,
                    port: data.port,
                    secure: data.secure,
                    auth: {
                        user: data.auth?.user || '',
                        pass: '' // Не показываем пароль
                    },
                    from: data.from,
                    fromName: data.fromName,
                    replyTo: data.replyTo
                });
            }
        } catch (error: any) {
            logger.error('Error fetching email settings:', error);
            if (error.response?.status !== 404) {
                message.error('Не удалось загрузить настройки почты');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values: any) => {
        try {
            setLoading(true);
            const data: any = {
                host: values.host,
                port: values.port,
                secure: values.secure || false,
                from: values.from,
                fromName: values.fromName,
                replyTo: values.replyTo,
                auth: {
                    user: values.auth.user
                }
            };

            // Добавляем пароль только если он указан
            if (values.auth.pass) {
                data.auth.pass = values.auth.pass;
            } else if (!settings) {
                // Если это первое создание настроек, пароль обязателен
                message.error('Укажите пароль для создания настроек');
                setLoading(false);
                return;
            }

            const response: any = await apiService.post('/email-settings', data, { timeout: 45000 });
            message.success('Настройки почты сохранены');
            if (response?.data?.warning) {
                message.warning(response.data.warning, 8);
            }
            await fetchSettings();
            // Очищаем поле пароля после успешного сохранения
            form.setFieldsValue({
                auth: {
                    user: values.auth.user,
                    pass: ''
                }
            });
        } catch (error: any) {
            logger.error('Error saving email settings:', error);
            const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
            const errorMsg = isTimeout
                ? 'Сервер не ответил вовремя. Проверьте доступность SMTP-сервера (хост, порт, firewall) и повторите попытку.'
                : (error.response?.data?.message || 'Не удалось сохранить настройки почты');
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleTest = async () => {
        try {
            const values = await form.validateFields(['from']);
            if (!values.from) {
                message.error('Укажите email для отправки тестового письма');
                return;
            }

            setTesting(true);
            await apiService.post('/email-settings/test', { email: values.from }, { timeout: 45000 });
            message.success('Тестовое письмо отправлено');
        } catch (error: any) {
            logger.error('Error sending test email:', error);
            const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
            const errorMessage = isTimeout
                ? 'Сервер не ответил вовремя. Проверьте доступность SMTP-сервера (хост, порт, firewall).'
                : (error.response?.data?.message ??
                    error.response?.data?.data?.message ??
                    error.message ??
                    'Не удалось отправить тестовое письмо');
            const isLongOrConfig =
                isTimeout ||
                errorMessage.includes('пароль приложения') ||
                errorMessage.includes('Application password') ||
                errorMessage.includes('Хост') ||
                errorMessage.includes('host') ||
                errorMessage.length > 80;
            message.error({
                content: errorMessage,
                duration: isLongOrConfig ? 12 : 3
            });
        } finally {
            setTesting(false);
        }
    };

    return (
        <div>
            <Title level={2}>
                <MailOutlined style={{ marginRight: 8 }} />
                Настройки почты
            </Title>

            <Card loading={loading} styles={{ body: { padding: '24px' } }}>
                <Alert
                    message="Настройки SMTP сервера"
                    description={
                        <div>
                            <div>Укажите параметры вашего SMTP сервера для отправки писем. Пароль будет зашифрован и сохранен в базе данных.</div>
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                <strong>Важно:</strong> Для некоторых почтовых сервисов (Gmail, Mail.ru, Yandex) требуется использовать пароль приложения вместо обычного пароля. 
                                Для Mail.ru: <a href="https://help.mail.ru/mail/security/protection/external" target="_blank" rel="noopener noreferrer">инструкция по созданию пароля приложения</a>
                            </div>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    initialValues={{
                        port: 587,
                        secure: false
                    }}
                >
                    <Form.Item
                        label="SMTP сервер"
                        name="host"
                        rules={[
                            { required: true, message: 'Укажите SMTP сервер' },
                            {
                                pattern: /^[^@]+$/,
                                message: 'Укажите имя хоста (например smtp.karate-online.ru), не email-адрес'
                            }
                        ]}
                        help="Имя хоста SMTP (например smtp.karate-online.ru или mail.karate-online.ru)"
                    >
                        <Input placeholder="smtp.karate-online.ru" />
                    </Form.Item>

                    <Form.Item
                        label="Порт"
                        name="port"
                        rules={[{ required: true, message: 'Укажите порт' }]}
                    >
                        <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label="Использовать SSL/TLS"
                        name="secure"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item label="Авторизация">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Form.Item
                                name={['auth', 'user']}
                                rules={[{ required: true, message: 'Укажите email для авторизации' }]}
                                style={{ marginBottom: 0 }}
                            >
                                <Input placeholder="your-email@gmail.com" />
                            </Form.Item>
                            <Form.Item
                                name={['auth', 'pass']}
                                rules={settings ? [] : [{ required: true, message: 'Укажите пароль' }]}
                                style={{ marginBottom: 0 }}
                                help="Для Gmail, Mail.ru, Yandex используйте пароль приложения"
                            >
                                <Input.Password 
                                    placeholder={settings ? 'Оставьте пустым, чтобы не менять пароль' : 'Пароль или пароль приложения'} 
                                />
                            </Form.Item>
                        </Space>
                    </Form.Item>

                    <Form.Item
                        label="Email отправителя"
                        name="from"
                        rules={[
                            { required: true, message: 'Укажите email отправителя' },
                            { type: 'email', message: 'Некорректный email' }
                        ]}
                    >
                        <Input placeholder="noreply@example.com" />
                    </Form.Item>

                    <Form.Item
                        label="Имя отправителя"
                        name="fromName"
                        rules={[{ required: true, message: 'Укажите имя отправителя' }]}
                    >
                        <Input placeholder="Система управления турнирами" />
                    </Form.Item>

                    <Form.Item
                        label="Email для ответа (необязательно)"
                        name="replyTo"
                        rules={[{ type: 'email', message: 'Некорректный email' }]}
                    >
                        <Input placeholder="support@example.com" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={loading}
                            >
                                Сохранить настройки
                            </Button>
                            <Button
                                icon={<SendOutlined />}
                                onClick={handleTest}
                                loading={testing}
                            >
                                Отправить тестовое письмо
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default EmailSettingsPage;

